import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import {
  getBouquetList,
  getChannelList,
  getCategoryList,
  getFeedbackList,
  getChannelNumberList,
  getCategoryChannelList,
  getCityGuideList,
} from '../../utils/cmsUtils';
import { getOfferList, getOfferCardsList } from '../../utils/ServicesAPIUtils';
import { set, sortBy } from 'lodash';
import {
  getActivityCategoriesList,
  getActivityList,
  getActivityTagsList,
} from '../../utils/activityCalendarAPIUtils';
import { decryptLocalStorage } from '../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../utils/loaderSlice';
import { notificationManager } from '../../utils/NotificationUtils';
import { logError } from '../../utils/commonUtils';
import { ENVIRONMENT } from '../../env/Configurations';

export const cmsSlice = createSlice({
  name: 'cms',
  initialState: {
    offerLoader: false,
    offers: [],
    cityGuideLoader: false,
    cityGuides: [],
    allCityGuideCategories: [],
    feedbackLoader: false,
    feedbacks: [],
    tvChannelBouquets: [],
    tvChannelCategorys: [],
    channelNumberList: {},
    offerCards: [],
    activityTagsList: [],
    activityList: [],
    activityCategories: [],
    activityCategoriesLoader: false,
  },
  reducers: {
    setOffers: (state, action) => {
      state.offerLoader = true;
      state.offers = [];
    },
    setOfferCards: (state, action) => {
      state.offerLoader = true;
      state.offerCards = [];
    },
    setOffersSuccess: (state, action) => {
      state.offerLoader = false;
      state.offers = action.payload;
    },
    setOfferCardsSuccess: (state, action) => {
      state.offerLoader = false;
      state.offerCards = action.payload;
    },
    setOffersError: (state, action) => {
      state.offerLoader = false;
      state.offers = [];
    },
    setCityGuides: (state, action) => {
      state.cityGuideLoader = true;
      state.cityGuides = [];
    },
    setCityGuidesSuccess: (state, action) => {
      state.cityGuideLoader = false;
      state.cityGuides = action.payload;
    },
    setCityGuidesError: (state, action) => {
      state.cityGuideLoader = false;
      state.cityGuides = [];
    },
    setAllCityGuideCategories: (state, action) => {
      state.allCityGuideCategories = action.payload;
    },
    setFeedbacks: (state, action) => {
      state.feedbackLoader = true;
      state.feedbacks = [];
    },
    setFeedbacksSuccess: (state, action) => {
      state.feedbackLoader = false;
      state.feedbacks = action.payload;
    },
    setFeedbacksError: (state, action) => {
      state.feedbackLoader = false;
      state.feedbacks = [];
    },
    setBouquets: (state, action) => {
      state.bouquetLoader = true;
      state.tvChannelBouquets = [];
    },
    setBouquetsError: (state, action) => {
      state.bouquetLoader = false;
      state.tvChannelBouquets = [];
    },
    setBouquetsSuccess: (state, action) => {
      state.bouquetLoader = false;
      state.tvChannelBouquets = action.payload;
    },
    setChannels: (state, action) => {
      state.channelLoader = true;
      state.tvChannels = [];
    },
    setChannelsError: (state, action) => {
      state.channelLoader = false;
      state.tvChannels = [];
    },
    setChannelsSuccess: (state, action) => {
      state.channelLoader = false;
      state.tvChannels = action.payload;
    },
    setCategorys: (state, action) => {
      state.categoryLoader = true;
      state.tvChannelCategorys = [];
    },
    setCategorysError: (state, action) => {
      state.categoryLoader = false;
      state.tvChannelCategorys = [];
    },
    setCategorysSuccess: (state, action) => {
      state.categoryLoader = false;
      state.tvChannelCategorys = action.payload;
    },
    setCategoryChannels: (state, action) => {
      state.categoryLoader = true;
      state.tvCategoryChannels = [];
    },
    setCategoryChannelsError: (state, action) => {
      state.categoryLoader = false;
      state.tvCategoryChannels = [];
    },
    setCategoryChannelsSuccess: (state, action) => {
      state.categoryLoader = false;
      state.tvCategoryChannels = action.payload;
    },
    setChannelNumberList: (state, action) => {
      state.channelNumberList = action.payload;
    },
    setActivityTagsList: (state, action) => {
      state.activityTagsList = action.payload;
    },
    setActivityList: (state, action) => {
      state.activityList = action.payload;
    },
    setActivityCategories: (state, action) => {
      state.activityCategoriesLoader = true;
      state.activityCategories = [];
    },
    setActivityCategoriesSuccess: (state, action) => {
      state.activityCategoriesLoader = false;
      state.activityCategories = action.payload || [];
    },
    setActivityCategoriesError: (state, action) => {
      state.activityCategoriesLoader = false;
      state.activityCategories = [];
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setOffers,
  setOfferCards,
  setOffersSuccess,
  setOffersError,
  setCityGuides,
  setAllCityGuideCategories,
  setCityGuidesSuccess,
  setCityGuidesError,
  setFeedbacks,
  setFeedbacksSuccess,
  setFeedbacksError,
  setBouquets,
  setBouquetsSuccess,
  setBouquetsError,
  setChannels,
  setChannelsSuccess,
  setChannelsError,
  setCategorys,
  setCategorysSuccess,
  setCategorysError,
  setCategoryChannels,
  setCategoryChannelsSuccess,
  setCategoryChannelsError,
  setChannelNumberList,
  setOfferCardsSuccess,
  setActivityTagsList,
  setActivityList,
  setActivityCategories,
  setActivityCategoriesSuccess,
  setActivityCategoriesError,
} = cmsSlice.actions;

// ------------------------------------------------------------------------------------------------------------------------ //
// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(setAllGroups([{}, {}]))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched

// Fetch all In Room Dining related details and set to global store
export const fetchAllBouquets = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    dispatch(setBouquets());
    const res = await getBouquetList(hotelId, getByLang);
    dispatch(setBouquetsSuccess(res.data.listBouquet));
  } catch (error) {
    dispatch(setBouquetsError());
    logError(hotelId, 'fetchAllBouquets', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAllChannels = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    dispatch(setChannels());
    const res = await getChannelList(hotelId, getByLang);
    const channelListSorted = res.data.listLiveTv;
    const channelListUpdatedStatus = channelListSorted.map((channel) => {
      let statusClass = '';
      let statusText = '';
      switch (channel.status) {
        case true:
          statusClass = 'pill-active';
          statusText = 'Active';
          break;
        default:
          statusClass = 'pill-inactive';
          statusText = 'Inactive';
          break;
      }
      return { ...channel, statusClass, statusText };
    });
    dispatch(setChannelsSuccess(channelListUpdatedStatus));
  } catch (error) {
    dispatch(setChannelsError());
    logError(hotelId, 'fetchAllChannels', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAllCategorys = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    dispatch(setCategorys());
    const res = await getCategoryList(hotelId, getByLang);
    dispatch(setCategorysSuccess(res.data.listTVCategory));
  } catch (error) {
    dispatch(setCategorysError());
    logError(hotelId, 'fetchAllCategorys', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAllCategoryChannels = (hotelId, categoryID) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    dispatch(setCategoryChannels());
    const res = await getCategoryChannelList(hotelId, categoryID, getByLang);
    dispatch(setCategoryChannelsSuccess(res.data.listChannelsByCatagoryID));
  } catch (error) {
    dispatch(setCategoryChannelsError());
    logError(hotelId, 'fetchAllCategoryChannels', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAllOffers = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    dispatch(setOffers());
    const res = await getOfferList(hotelId, getByLang);
    const offersDetails = res?.data?.getOffersDetails?.map((e) => {
      let status = '';
      let statusClass = '';
      let displaySchedule = '';
      if (e?.duration?.alwaysActive) {
        statusClass = 'pill-active';
        status = 'Live';
        displaySchedule = 'Always On';
      } else {
        displaySchedule = `${moment(e?.duration?.startDate, 'DD-MM-YYYY').format(
          'DD MMM, YYYY',
        )} - ${moment(e.duration.endDate, 'DD-MM-YYYY').format('DD MMM, YYYY')}`;
        const startTime = `${e?.duration?.startDate} ${
          e?.duration?.startTime ? e?.duration?.startTime : '00:00'
        }`;
        const endTime = `${e?.duration?.endDate} ${
          e?.duration?.endTime ? e?.duration?.endTime : '23:59'
        }`;
        const isBefore = moment(startTime, 'DD-MM-YYYY HH:mm').isBefore(moment());
        const isAfter = moment(endTime, 'DD-MM-YYYY HH:mm').isAfter(moment());
        if (isAfter && isBefore) {
          status = 'Live';
          statusClass = 'pill-active';
        } else if (isBefore && !isAfter) {
          status = 'Completed';
          statusClass = 'pill-inactive';
        } else if (!isBefore && isAfter) {
          status = 'Scheduled';
          statusClass = 'scheduled';
        }
      }

      if (!e.isActive) {
        status = 'Inactive';
        statusClass = 'pill-inactive';
      }
      return { ...e, status, statusClass, displaySchedule };
    });
    dispatch(setOffersSuccess(offersDetails));
  } catch (error) {
    dispatch(setOffersError());
    logError(hotelId, 'fetchAllOffers', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAllOfferCards = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    dispatch(setOfferCards());
    const res = await getOfferCardsList(hotelId, getByLang);
    const offersDetails = res.data.getOffersCardDetails.map((e) => {
      let status = '';
      let statusClass = '';
      if (e?.schedule?.alwaysActive) {
        statusClass = 'pill-active';
        status = 'Live';
      } else {
        if (e.schedule?.scheduleType === 'ONE_TIME') {
          const date = e.schedule?.oneTime?.date;

          const time = e.schedule?.oneTime?.time;
          const startTime = `${date} ${time ? time : '00:00'}`;

          const isBefore = moment(startTime, 'DD-MM-YYYY HH:mm').isBefore(moment());
          const isAfter = moment(startTime, 'DD-MM-YYYY HH:mm').isAfter(moment());

          if (isAfter && isBefore) {
            status = 'Live';
            statusClass = 'pill-active';
          } else if (isBefore && !isAfter) {
            status = 'Completed';
            statusClass = 'pill-inactive';
          } else if (!isBefore && isAfter) {
            status = 'Scheduled';
            statusClass = 'scheduled';
          }
        } else {
          const startTime = `${e.schedule?.recurring?.startDate} ${
            e.schedule?.recurring?.recursTime ? e.schedule?.recurring?.recursTime : '00:00'
          }`;
          const endTime = `${e.schedule?.recurring?.endDate} ${
            e.schedule?.recurring?.recursTime ? e.schedule?.recurring?.recursTime : '23:59'
          }`;
          const isBefore = moment(startTime, 'DD-MM-YYYY HH:mm').isBefore(moment());
          const isAfter = moment(endTime, 'DD-MM-YYYY HH:mm').isAfter(moment());
          if (isAfter && isBefore) {
            status = 'Live';
            statusClass = 'pill-active';
          } else if (isBefore && !isAfter) {
            status = 'Completed';
            statusClass = 'pill-inactive';
          } else if (!isBefore && isAfter) {
            status = 'Scheduled';
            statusClass = 'scheduled';
          }
        }
      }

      if (!e?.isActive) {
        status = 'Inactive';
        statusClass = 'pill-inactive';
      }
      let type = e.offerCardType === 'SCRATCH_CARD' ? 'Scratch Card' : 'Pop-up Card';
      let recipient = e.offerRecipient !== 'all' ? 'In-house Guests Only' : 'All Guests';

      return { ...e, status, statusClass, type, recipient };
    });
    dispatch(setOfferCardsSuccess(offersDetails));
  } catch (error) {
    logError(hotelId, 'fetchAllOfferCards', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAllCityGuide = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    dispatch(setCityGuides());
    const res = await getCityGuideList(hotelId, getByLang);
    dispatch(setCityGuidesSuccess(res?.data?.getCityGuidesDetails?.cityGuides));
    dispatch(setAllCityGuideCategories(res?.data?.getCityGuidesDetails?.categories));
  } catch (error) {
    dispatch(setCityGuidesError());
    logError(hotelId, 'fetchAllCityGuide', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAllFeedback = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    dispatch(setFeedbacks());
    const res = await getFeedbackList(hotelId, getByLang);
    dispatch(setFeedbacksSuccess(res?.data?.listFeedback));
  } catch (error) {
    dispatch(setFeedbacksError());
    logError(hotelId, 'fetchAllFeedback', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchChannelNumberList = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    const res = await getChannelNumberList(hotelId, getByLang);
    dispatch(setChannelNumberList(JSON.parse(res?.data?.getChannelListMapping?.jsonResponse)));
  } catch (error) {
    dispatch(setFeedbacksError());
    logError(hotelId, 'fetchChannelNumberList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};
export const fetchActivityTagsList = (hotelId) => async (dispatch) => {
  const selectedLanguage = decryptLocalStorage('lang');
  const language = selectedLanguage !== 'en' ? selectedLanguage : '';
  try {
    dispatch(stepUpLoaderCount());
    const res = await getActivityTagsList(hotelId, language);
    dispatch(setActivityTagsList(res?.data?.getActivityTags || []));
  } catch (error) {
    logError(hotelId, 'fetchActivityTagsList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

const handleUpdateDateTime = (currentActivity) => {
  let currentActivityDate = moment();
  if (currentActivity?.schedule?.scheduleType === 'RECURRING') {
    currentActivityDate = moment(
      currentActivity?.schedule?.recurring?.startDate + ' ' + currentActivity?.schedule?.startTime,
      'DD-MM-YYYY HH:mm',
    );
  } else if (currentActivity?.schedule?.scheduleType === 'ONE_TIME') {
    currentActivityDate = moment(
      currentActivity?.schedule?.oneTime?.date + ' ' + currentActivity?.schedule?.startTime,
      'DD-MM-YYYY HH:mm',
    );
  } else {
    const date = new Date(currentActivity?.createdAt * 1000);
    const startTime = currentActivity?.schedule?.startTime?.split(':');
    currentActivityDate = date.setHours(startTime[0]);
    currentActivityDate = moment(date.setMinutes(startTime[1]));
  }
  return new Date(currentActivityDate.toString());
};

export const fetchActivityList = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepDownLoaderCount());
    const selectedLanguage = decryptLocalStorage('lang');
    const language = selectedLanguage !== 'en' ? selectedLanguage : '';
    const res = await getActivityList(hotelId, language);
    const activitySortOnDate = [...(res?.data?.getActivities || [])]?.sort(
      (currentActivity, nextActivity) => {
        let currentActivityDate = handleUpdateDateTime(currentActivity),
          nextActivityDate = handleUpdateDateTime(nextActivity);
        return currentActivityDate - nextActivityDate;
      },
    );
    dispatch(setActivityList(activitySortOnDate));
  } catch (error) {
    logError(hotelId, 'fetchActivityList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAllActivityCategories = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    dispatch(setActivityCategories());
    const res = await getActivityCategoriesList(hotelId, getByLang);
    dispatch(setActivityCategoriesSuccess(res?.data?.getActivityCategories));
  } catch (error) {
    dispatch(setActivityCategoriesError());
    logError(hotelId, 'fetchAllActivityCategories', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

// ------------------------------------------------------------------------------------------------------------------------ //

export const offerSelector = (state) => state.cms.offers;
export const offerCardsSelector = (state) => state.cms.offerCards;
export const offerLoaderSelector = (state) => state.cms.offerLoader;
export const cityGuideSelector = (state) => state.cms.cityGuides;
export const allCityGuideCategoriesSelector = (state) => state.cms.allCityGuideCategories;
export const cityGuideLoaderSelector = (state) => state.cms.cityGuideLoader;
export const feedbackSelector = (state) => state.cms.feedbacks;
export const feedbackLoaderSelector = (state) => state.cms.feedbackLoader;
export const bouquetSelector = (state) => state.cms.tvChannelBouquets;
export const bouquetLoaderSelector = (state) => state.cms.bouquetLoader;
export const channelSelector = (state) => state.cms.tvChannels;
export const channelLoaderSelector = (state) => state.cms.channelLoader;
export const categorySelector = (state) => state.cms.tvChannelCategorys;
export const categoryChannelSelector = (state) => state.cms.tvCategoryChannels;
export const categoryLoaderSelector = (state) => state.cms.categoryLoader;
export const channelNumberSelector = (state) => state.cms.channelNumberList;
export const allActivityCalendarCategoriesSelector = (state) => state?.cms?.activityCategories;
export const activityCalendarSelector = (state) => state.cms.activityCategories;

export default cmsSlice.reducer;
